import React, { useEffect, useRef, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import {
    Checkbox,
    CustomModal,
    Flex,
    Icon,
    Input,
    Select,
    TextArea,
    UiButton,
    UiText,
} from '@shared/ui';

import cls from './GenerateEstimateModal.module.css';
import { IFormGenerateEstimate } from '../../lib/model/types';

interface GenerateModalProps {
    isOpen: boolean;
    onHideModal: () => void;
    formGenerateEstimate: IFormGenerateEstimate;
    onFormGenerateEstimate: (name: string, value: string | boolean) => void;
    onGenerateEstimate: () => Promise<void>;
    onSelect?: (value: string) => void;
    errorsForm?: {
        constructionTypeTitle: null | string;
        supplierTitle: null | boolean;
    };
}

export const GenerateEstimateModal = ({
    isOpen,
    onHideModal,
    formGenerateEstimate,
    onFormGenerateEstimate,
    onGenerateEstimate,
    onSelect,
    errorsForm,
}: GenerateModalProps) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <CustomModal contentClasses={cls.contentBlock} isOpen={isOpen} onHideModal={onHideModal}>
            <CustomModal.Header>
                <div className={cls.GenerateModalHeader}>
                    {t('GenerateEstimateModal.return.CustomModal.Header')}
                    <Icon iconName="aiStars" />
                </div>
            </CustomModal.Header>
            <CustomModal.Body classes={cls.modalBody}>
                <Flex gap={12} direction="column" className={cls.typeWrapper}>
                    <UiText
                        align="left"
                        weight="bold"
                        variant="boldPlain"
                        className={cls.typeTitle}
                    >
                        <Trans i18nKey="GenerateEstimateModal.return.CustomModal.Body.typeWrapper.UiText.typeTitle" />
                    </UiText>
                    <Input
                        required
                        innerRef={inputRef}
                        maxLength={255}
                        error={errorsForm.constructionTypeTitle}
                        name="constructionTypeTitle"
                        onValueChange={onFormGenerateEstimate}
                        value={formGenerateEstimate.constructionTypeTitle}
                        classes={[cls.input]}
                        label={t('TypeConstructionStep.return.Input.label')}
                        placeholder={t('TypeConstructionStep.return.Input.placeholder')}
                    />
                    <Input
                        maxLength={255}
                        name="dimensions"
                        onValueChange={onFormGenerateEstimate}
                        value={formGenerateEstimate.dimensions}
                        classes={[cls.input]}
                        label={t(
                            'GenerateEstimateModal.return.CustomModal.Body.typeWrapper.Input.dimensions.label'
                        )}
                        placeholder={t(
                            'GenerateEstimateModal.return.CustomModal.Body.typeWrapper.Input.dimensions.placeholder'
                        )}
                    />
                </Flex>

                <Flex gap={12} direction="column" align="start" className={cls.descriptionWrapper}>
                    <UiText weight="bold" variant="boldPlain" className={cls.descriptionTitle}>
                        {t(
                            'GenerateEstimateModal.return.CustomModal.Body.descriptionWrapper.UiText.typeTitle'
                        )}
                    </UiText>
                    <TextArea
                        label={t('ObjectDetailsStep.return.TextArea.label')}
                        placeholder={t('ObjectDetailsStep.return.TextArea.placeholder')}
                        inputName="description"
                        maxLength={2000}
                        classes={cls.textarea}
                        onValueChange={onFormGenerateEstimate}
                        value={formGenerateEstimate.description}
                    />
                </Flex>

                <div className={cls.calculationWrapper}>
                    <UiText classes={cls.calculationTitle} variant="mediumPlain">
                        {t(
                            'GenerateEstimateModal.return.CustomModal.Body.calculationWrapper.calculationTitle'
                        )}
                    </UiText>
                    <div className={cls.calculation}>
                        <Checkbox
                            classes={cls.checkbox}
                            isActive={formGenerateEstimate.needUseDirectories}
                            title={t('CalculationSettingsStep.return.Switcher.title.isUseMyPrices')}
                            onClick={() =>
                                onFormGenerateEstimate(
                                    'needUseDirectories',
                                    !formGenerateEstimate.needUseDirectories
                                )
                            }
                        />
                        <Checkbox
                            classes={cls.checkbox}
                            isActive={formGenerateEstimate.isUseSupplierPrices}
                            title={t(
                                'CalculationSettingsStep.return.Switcher.title.isUseSupplierPrices'
                            )}
                            onClick={() =>
                                onFormGenerateEstimate(
                                    'isUseSupplierPrices',
                                    !formGenerateEstimate.isUseSupplierPrices
                                )
                            }
                        />
                        {formGenerateEstimate.isUseSupplierPrices && (
                            <Select
                                isSmartPosition
                                isError={errorsForm.supplierTitle}
                                selectRootClassName={cls.selectRootClassName}
                                classes={cls.select}
                                placeholder={t('CalculationSettingsStep.return.Select.placeholder')}
                                value={formGenerateEstimate.supplierTitle}
                                options={[
                                    {
                                        value: 'Лемана ПРО',
                                        label: 'Лемана ПРО',
                                    },
                                    {
                                        value: 'Петрович',
                                        label: 'Петрович',
                                    },
                                    {
                                        value: 'Максидом',
                                        label: 'Максидом',
                                    },
                                    {
                                        value: 'Вимос',
                                        label: 'Вимос',
                                    },
                                    {
                                        value: 'Сатурн',
                                        label: 'Сатурн',
                                    },
                                    {
                                        value: 'ОБИ',
                                        label: 'ОБИ',
                                    },
                                    {
                                        value: 'Все Инструменты',
                                        label: 'Все Инструменты',
                                    },
                                    {
                                        value: 'ЭТМ',
                                        label: 'ЭТМ',
                                    },
                                ]}
                                onChange={onSelect}
                            />
                        )}
                    </div>
                </div>
            </CustomModal.Body>
            <CustomModal.Footer>
                <UiButton
                    disabled={!!errorsForm.supplierTitle || !!errorsForm.constructionTypeTitle}
                    onClick={onGenerateEstimate}
                    btnText={t('CalculationSettingsStep.return.UiButton.generate')}
                />
            </CustomModal.Footer>
        </CustomModal>
    );
};
